/* ------------------------------------------------------------------
   Brand repair pages — one entry per brand.
   Each page lives at /brands/<slug>-repair, e.g. /brands/sub-zero-repair
   Every brand has its own copy so Google treats each page as unique.
------------------------------------------------------------------- */

export const BRAND_PAGES = [
    {
      slug: "sub-zero",
      name: "Sub-Zero",
      titleTag: "Sub-Zero Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "Sub-Zero refrigerator, freezer and wine storage repair in Plainfield, Naperville, Aurora & Joliet. Built-in and column models. $99 diagnostic. Call (331) 600-4935.",
      h1: "Sub-Zero Refrigerator Repair in Plainfield, Naperville & Nearby",
      lead:
        "A Sub-Zero is built to last decades, so a repair is almost always worth it. We repair Sub-Zero built-in, column and undercounter refrigerators, freezers and wine storage in homes across Plainfield, Naperville, Aurora, Joliet and the western suburbs.",
      appliances: [
        "Built-in side-by-side and over-and-under refrigerators",
        "Column refrigerators and freezers",
        "Undercounter refrigerators, drawers and ice makers",
        "Wine storage and beverage centers",
      ],
      problems: [
        ["Fresh food section warm, freezer fine", "Many Sub-Zero models run two separate cooling systems, so one side can fail while the other keeps working. Common causes are an evaporator fan, a defrost problem or a failing sealed-system part on that side."],
        ["Refrigerator running constantly or feels hot at the top", "Built-in models have the compressor and condenser at the top behind the grille. A clogged condenser makes the system work harder and can shorten its life. Cleaning it is a quick part of most service visits."],
        ["Ice maker not making ice", "Usually a water inlet valve, a frozen fill tube, a clogged filter or the ice maker module itself."],
        ["Water under the unit or frost buildup", "Often a clogged defrost drain or a door gasket that no longer seals. On a large built-in door, a worn gasket lets in a lot of warm air."],
        ["Wine storage not holding temperature", "Temperature swings in a wine unit are usually a sensor, fan or control issue. We check it before your collection is at risk."],
      ],
      faqs: [
        ["Is it worth repairing an older Sub-Zero?", "Usually yes. Sub-Zero units are designed for a long service life and cost far more to replace than most refrigerators. We give you a written quote so you can decide."],
        ["Do you repair Sub-Zero wine coolers?", "Yes. We repair Sub-Zero wine storage and beverage centers, along with their refrigerators and freezers."],
      ],
    },
    {
      slug: "wolf",
      name: "Wolf",
      titleTag: "Wolf Range & Oven Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "Wolf range, oven, cooktop and rangetop repair in Plainfield, Naperville, Aurora & Joliet. Gas, dual fuel and induction. $99 diagnostic. Call (331) 600-4935.",
      h1: "Wolf Range, Oven & Cooktop Repair in Plainfield, Naperville & Nearby",
      lead:
        "When a Wolf range stops lighting or an oven won't hold temperature, cooking at home grinds to a halt. We repair Wolf gas, dual fuel and induction ranges, rangetops, cooktops and wall ovens across Plainfield, Naperville, Aurora, Joliet and nearby towns.",
      appliances: [
        "Gas, dual fuel and induction ranges",
        "Rangetops and cooktops",
        "Wall ovens, convection steam ovens and speed ovens",
        "Warming drawers and microwave drawers",
      ],
      problems: [
        ["Igniters clicking nonstop", "Often moisture from a spill or cleaning around the burner, but it can also be a faulty spark module or burner switch. We find which one so the clicking doesn't come back."],
        ["Burner won't light or flame is uneven", "Common causes are a clogged burner port, a misaligned burner cap, a weak igniter or a gas valve problem."],
        ["Oven slow to preheat or not heating", "On gas ovens the oven igniter is a frequent culprit. On dual fuel and electric ovens, a bake or broil element or a temperature sensor is more likely."],
        ["Oven temperature off", "If recipes burn or undercook, the oven may need a sensor, control or calibration check."],
        ["Convection fan noisy or not running", "A worn fan motor or a loose fan blade usually causes grinding or rattling during convection cooking."],
      ],
      faqs: [
        ["Do you repair Wolf dual fuel ranges?", "Yes. Dual fuel ranges have a gas cooktop and an electric oven, and we repair both sides."],
        ["Do you also repair Sub-Zero?", "Yes. Many kitchens have both, and we repair Sub-Zero refrigeration and Wolf cooking appliances on the same visit when needed."],
      ],
    },
    {
      slug: "thermador",
      name: "Thermador",
      titleTag: "Thermador Appliance Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "Thermador range, oven, refrigerator and dishwasher repair in Plainfield, Naperville, Aurora & Joliet. $99 diagnostic, 90-day warranty. Call (331) 600-4935.",
      h1: "Thermador Appliance Repair in Plainfield, Naperville & Nearby",
      lead:
        "Thermador kitchens are often built around a pro-style range with Star burners and matching column refrigeration. We repair Thermador ranges, rangetops, wall ovens, refrigerators and dishwashers across Plainfield, Naperville, Aurora, Joliet and the surrounding suburbs.",
      appliances: [
        "Pro Grand and Pro Harmony ranges and rangetops",
        "Wall ovens and steam ovens",
        "Freedom column and built-in refrigerators",
        "Dishwashers",
      ],
      problems: [
        ["Star burner clicking or won't light", "Moisture, a dirty burner, a weak igniter or a faulty spark module can all cause it. The star-shaped burners have more ports, so cleaning and alignment matter."],
        ["Oven not heating or heating unevenly", "Usually an oven igniter on gas models, or an element, sensor or convection fan on electric models."],
        ["Column refrigerator not cooling", "We check fans, defrost components, sensors and controls to find why the column isn't holding temperature."],
        ["Ice maker or water dispenser not working", "Often a frozen line, inlet valve, filter or ice maker module."],
        ["Dishwasher not draining or showing an error", "A clogged filter or drain pump is common. Error codes help narrow it down, and we confirm the cause before quoting."],
      ],
      faqs: [
        ["Do you repair Thermador dishwashers?", "Yes. We repair Thermador dishwashers along with its ranges, ovens and refrigerators."],
        ["Can you fix a Thermador range that clicks after cleaning?", "Often it dries out on its own within a few hours. If it keeps clicking, the spark module or a burner switch may need replacing, and we can diagnose it."],
      ],
    },
    {
      slug: "viking",
      name: "Viking",
      titleTag: "Viking Appliance Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "Viking range, oven and refrigerator repair in Plainfield, Naperville, Aurora & Joliet. Pro-style and built-in models. $99 diagnostic. Call (331) 600-4935.",
      h1: "Viking Range & Refrigerator Repair in Plainfield, Naperville & Nearby",
      lead:
        "Viking's heavy-duty, pro-style appliances are made to cook hard every day, but igniters, elements and cooling parts still wear out. We repair Viking ranges, rangetops, wall ovens, refrigerators and dishwashers across Plainfield, Naperville, Aurora, Joliet and nearby towns.",
      appliances: [
        "Pro-style gas and dual fuel ranges",
        "Rangetops and cooktops",
        "Wall ovens",
        "Built-in refrigerators and freezers",
        "Dishwashers",
      ],
      problems: [
        ["Burners won't light or keep clicking", "A dirty or wet burner, a cracked igniter or a failing spark module are the usual causes."],
        ["Oven won't reach temperature", "A worn oven igniter is very common on gas ovens. It can glow without opening the gas valve. Electric ovens more often need an element or sensor."],
        ["Oven door or hinges not closing tight", "Worn hinges let heat escape, which slows cooking and can overheat the knobs and controls."],
        ["Refrigerator warm or icing up", "Fans, defrost parts and door seals are the first things we check on Viking built-in refrigeration."],
        ["Dishwasher leaking or not cleaning", "Spray arms, pumps, seals and filters are common causes."],
      ],
      faqs: [
        ["Do you repair older Viking ranges?", "Yes. Many older Viking ranges are worth repairing, and we tell you honestly if a part is no longer available."],
        ["Do you repair Viking refrigerators too?", "Yes. We repair Viking built-in refrigerators and freezers, not just cooking appliances."],
      ],
    },
    {
      slug: "miele",
      name: "Miele",
      titleTag: "Miele Dishwasher, Washer & Dryer Repair Plainfield, IL | AT Appliance",
      description:
        "Miele dishwasher, washer, dryer and oven repair in Plainfield, Naperville, Aurora & Joliet. $99 diagnostic, 90-day warranty. Call (331) 600-4935.",
      h1: "Miele Dishwasher, Washer & Dryer Repair in Plainfield, Naperville & Nearby",
      lead:
        "Miele appliances are precise machines, and most problems come down to a filter, pump, sensor or seal rather than a major failure. We repair Miele dishwashers, washers, dryers and ovens across Plainfield, Naperville, Aurora, Joliet and the western suburbs.",
      appliances: [
        "Dishwashers",
        "Front-load washers",
        "Vented and heat pump dryers",
        "Wall ovens and steam ovens",
      ],
      problems: [
        ["Dishwasher not draining or showing a fault", "Often a blocked filter or drain pump. We read the fault, confirm the cause and quote the repair."],
        ["Dishwasher leaving dishes dirty", "Spray arms, filters, the circulation pump or water supply can all reduce cleaning."],
        ["Washer not draining or door won't unlock", "Usually a clogged drain filter or pump. The door stays locked until the water drains, so the two problems often show up together."],
        ["Dryer taking too long", "On heat pump dryers, clogged lint and plinth filters are the most common cause. On vented models, a blocked vent or a heating fault is more likely."],
        ["Oven or steam oven errors", "Sensors, door switches and water systems on steam ovens are common repair points."],
      ],
      faqs: [
        ["Do you repair Miele heat pump dryers?", "Yes. Heat pump dryers work differently from vented dryers, and we diagnose both."],
        ["Should I repair or replace my Miele dishwasher?", "Most Miele dishwasher problems are repairable for much less than a replacement. We give you a written quote before any work."],
      ],
    },
    {
      slug: "dacor",
      name: "Dacor",
      titleTag: "Dacor Range & Oven Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "Dacor range, wall oven, cooktop and refrigerator repair in Plainfield, Naperville, Aurora & Joliet. $99 diagnostic, 90-day warranty. Call (331) 600-4935.",
      h1: "Dacor Range, Oven & Refrigerator Repair in Plainfield, Naperville & Nearby",
      lead:
        "Dacor appliances combine luxury design with a lot of electronics, so an accurate diagnosis matters before ordering parts. We repair Dacor ranges, wall ovens, cooktops and refrigerators across Plainfield, Naperville, Aurora, Joliet and nearby towns.",
      appliances: [
        "Gas, dual fuel and induction ranges",
        "Wall ovens",
        "Gas and induction cooktops",
        "Column and built-in refrigerators",
      ],
      problems: [
        ["Oven not heating or slow to preheat", "Common causes are an element, igniter, temperature sensor or relay on the control board."],
        ["Touch controls not responding", "A control panel, wiring or board fault can stop the controls. We test before replacing expensive electronic parts."],
        ["Cooktop burners clicking or not lighting", "Moisture, a dirty burner or a spark module issue are usually to blame."],
        ["Induction cooktop not heating a pan", "Cookware, a power issue or an induction board fault. We rule out the simple causes first."],
        ["Refrigerator not cooling", "We check fans, defrost parts, sensors and the sealed system."],
      ],
      faqs: [
        ["Do you repair Dacor wall ovens?", "Yes, including single and double wall ovens."],
        ["Are Dacor parts expensive?", "Some electronic parts can be, which is why we confirm the cause before ordering. Our 50/50 payment plan means you only pay half when we order the part."],
      ],
    },
    {
      slug: "bertazzoni",
      name: "Bertazzoni",
      titleTag: "Bertazzoni Range Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "Bertazzoni range, cooktop and oven repair in Plainfield, Naperville, Aurora & Joliet. Gas, dual fuel and induction. $99 diagnostic. Call (331) 600-4935.",
      h1: "Bertazzoni Range & Oven Repair in Plainfield, Naperville & Nearby",
      lead:
        "Bertazzoni's Italian ranges are built around powerful burners and large ovens. When a burner won't light or the oven won't heat, we can help. We repair Bertazzoni ranges, rangetops, cooktops and wall ovens across Plainfield, Naperville, Aurora, Joliet and nearby towns.",
      appliances: [
        "Gas, dual fuel and induction ranges",
        "Rangetops and cooktops",
        "Wall ovens",
      ],
      problems: [
        ["Burner lights then goes out", "Bertazzoni burners use a flame safety device. If the flame goes out when you release the knob, the thermocouple or its position near the flame is often the cause."],
        ["Burners won't spark", "A wet or dirty igniter, a worn switch or the spark module."],
        ["Oven not heating", "On gas ovens, the oven igniter or safety valve. On electric ovens, an element or thermostat."],
        ["Oven temperature uneven", "A convection fan, a sensor or a door seal letting heat escape."],
      ],
      faqs: [
        ["Why does my Bertazzoni burner go out when I let go of the knob?", "Holding the knob keeps gas flowing while the safety device heats up. If the flame dies on release, the thermocouple or its alignment usually needs attention."],
        ["Do you repair Bertazzoni induction ranges?", "Yes, along with gas and dual fuel models."],
      ],
    },
    {
      slug: "jennair",
      name: "JennAir",
      titleTag: "JennAir Appliance Repair Plainfield & Naperville, IL | AT Appliance",
      description:
        "JennAir range, wall oven, downdraft cooktop and refrigerator repair in Plainfield, Naperville, Aurora & Joliet. $99 diagnostic. Call (331) 600-4935.",
      h1: "JennAir Appliance Repair in Plainfield, Naperville & Nearby",
      lead:
        "Many homes in our area have JennAir wall ovens, downdraft cooktops and built-in refrigerators from the original kitchen. We repair JennAir cooking and refrigeration appliances across Plainfield, Naperville, Aurora, Joliet and the western suburbs.",
      appliances: [
        "Ranges and downdraft cooktops",
        "Single and double wall ovens",
        "Built-in refrigerators",
        "Dishwashers",
      ],
      problems: [
        ["Downdraft vent not pulling smoke", "A greasy filter, a blocked duct or a weak blower motor."],
        ["Wall oven not heating", "A bake or broil element, a temperature sensor or a control board relay."],
        ["Oven control showing an error", "We read the code and test the sensor, door lock and control before replacing parts."],
        ["Refrigerator ice maker or water not working", "An inlet valve, frozen line, filter or ice maker module."],
        ["Dishwasher not draining", "Usually a clogged filter, drain hose or drain pump."],
      ],
      faqs: [
        ["Do you repair older JennAir downdraft cooktops?", "Yes. Many older downdraft units are still repairable, and we tell you if a part is no longer made."],
        ["Do you repair JennAir double wall ovens?", "Yes, both the upper and lower ovens."],
      ],
    },
  ];
  
  export const brandPath = (slug) => `/brands/${slug}-repair`;
  export const findBrand = (slug) => BRAND_PAGES.find((b) => b.slug === slug);